import React, { useContext, useEffect, useRef, useState } from "react";
import { makeStyles, styled } from "@mui/styles";
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Grid,
  Paper,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Autocomplete,
  TextField,
  Chip,
  Button,
  Input,
  Stack,
  IconButton,
  Snackbar,
  Alert,
  ListSubheader,
  List,
  ListItem,
  CircularProgress,
  Hidden,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormHelperText,
} from "@mui/material";
import CKEditor from "ckeditor4-react";
import { v1 as uuidv1 } from "uuid";
import { useNavigate, useParams } from "react-router-dom";

//Date related importes
import dayjs from "dayjs";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

//... Imports icons
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VerifiedIcon from "@mui/icons-material/Verified";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DraftsIcon from "@mui/icons-material/Drafts";
import SendIcon from "@mui/icons-material/Send";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ReplyIcon from "@mui/icons-material/Reply";
import ClearIcon from "@mui/icons-material/Clear";

//... service importe
import { uploadGameScoreFileToBlob } from "../../services";
import { EmailNotificationService } from "../../services/notification.Service";

//... Context provider importes
import AppDataContext from "../../common/appContext";

//... Shared component imports.
import RouteConstants from "../../common/RouteConstants";
import Loader from "../../sharedComponents/loader/loader";

const useStyles = makeStyles((theme) => ({
  appbarBackground: {
    backgroundColor: "#fff",
    color: "#1d428a",
  },
  title: {
    flexGrow: 1,
  },
  centerContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column", // Align content vertically
  },
  paperContainer: {
    padding: "20px",
    width: "80%", // Adjust as needed
    margin: "10px",
  },
  sectionContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "16px",
  },
  inputContainer: {
    width: "48%",
    paddingLeft: "1rem",
  },
  buttonContainer: {
    marginTop: "20px",
  },
  loaderOverlay: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.7)",
    width: "100%",
    height: "100%",
    zIndex: theme.zIndex.modal + 1, // Make sure it's above other content
  },
}));

const StyledBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  margin: "auto",
});

const UploadContainer = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  width: "100%",
  marginBottom: "16px",
});

const StyledList = styled(List)({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const StyledListItem = styled(ListItem)({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  padding: "8px",
  borderRadius: "4px",
  backgroundColor: "#f0f0f0",
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EmailNotification = () => {
  const classes = useStyles();
  const params = useParams();
  const navigate = useNavigate();

  //... Context
  const { user } = useContext(AppDataContext);

  //... List data holders.
  const [recipientList, setRecipientList] = useState([]);
  const [emailStatusList, setEmailStatusList] = useState([]);
  const [emailFromList, setEmailFromList] = useState([]);

  //... Form data holders.
  const [selectedRecipient, setSelectedRecipient] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedCustomNames, setSelectedCustomNames] = useState(null);
  const [selectedFromName, setSSelectedFromName] = useState("");
  const [selectedSubjectName, setSSelectedSubjectName] = useState("");
  const [selectedEmailBody, setSelectedEmailBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [startDateTime, setStartDateTime] = useState(null);
  const [isValidEmailIds, setIsValidEmailIds] = useState("");

  //... Error data holders
  const [customEmailError, setCustomEmailError] = useState("");
  const [isValidRecipients, setIsValidRecipients] = useState(false);
  const [isValidUser, setIsValidUser] = useState(false);
  const [fromNameError, setFromNameError] = useState(false);
  const [startDateTimeError, setStartDateTimeError] = useState(false);
  const [startDateTimeErrorColor, setStartDateTimeErrorColor] = useState("");
  const [subjectNameError, setSubjectNameError] = useState(false);
  const [emailBodyError, setEmailBodyError] = useState(false);

  //... Alert data holders
  const [alert, setAlert] = useState(false);
  const [alertType, setAlertType] = useState("warning");
  const [alertMessage, setAlertMessage] = useState("");

  //... Loader data holders
  const [loading, setLoading] = useState(false);
  const [editorLoading, setEditorLoading] = useState(true);

  //... Dialog data holders.
  const [openNavigationConfirmDialog, setOpenNavigationConfirmDialog] =
    useState(false);

  useEffect(() => {
    // Simulate CKEditor initialization time
    const delay = setTimeout(() => {
      setEditorLoading(false);
    }, 4000); // Adjust the delay time as needed

    return () => clearTimeout(delay);
  }, []);

  useEffect(() => {
    async function fetchData() {
      await getEnumerationData();

      if (params?.notificationID > 0) {
        getNotificationDetailsByID(params.notificationID);
      }
    }

    fetchData();
  }, [params.notificationID]);

  //Handlers
  const handleRecipientChange = (event) => {
    const selectedValues = event.target.value;
    // If "None" is selected, only keep "None" in the selected values
    if (selectedValues.includes(0)) {
      setSelectedRecipient([0]);
    } else if (selectedValues.includes(1)) {
      // If "All" is selected, only keep "All" in the selected values
      setSelectedRecipient([1]);
    } else {
      // If other options are selected, set the selected values accordingly
      setSelectedRecipient(selectedValues);
    }
  };

  const handleUserChange = (event, value) => {
    setSelectedUsers(value);
  };

  const handleCustomChange = (e) => {
    setIsValidEmailIds("");
    setSelectedCustomNames(e.target.value);
  };

  const handleCustomBlur = () => {
    validateEmails(selectedCustomNames);
  };

  const handleEditorChange = (event) => {
    const data = event.editor.getData();
    setSelectedEmailBody(data);
  };

  const handleFileChange = (e) => {
    const files = [];

    if (e.target.files && e.target.files.length > 0) {
      Array.from(e.target.files).forEach((file) => {
        const blob = file.slice(0, file.size, file.type);
        const extension = file.name.substring(file.name.lastIndexOf(".") + 1);

        // Set new filename.
        const displayFileName = file.name.replace(`.${extension}`, "");
        const newFileName = uuidv1() + "." + extension;
        const newFile = new File([blob], newFileName, { type: file.type });
        files.push({
          displayFileName: displayFileName,
          fileName: newFile,
          finalFileName: file.name,
          isAlreadyUploaded: false,
        });
      });
    }

    setAttachments((prevAttach) => [...prevAttach, ...files]);
  };

  const handleRemoveFile = (index) => {
    const updatedFiles = [...attachments];
    updatedFiles.splice(index, 1);
    setAttachments(updatedFiles);
  };

  const handleStartDateTimeChange = (newValue) => {
    if (newValue !== null) {
      if (newValue.isValid()) {
        setStartDateTime(dayjs(newValue));
        setStartDateTimeError(false);
        setStartDateTimeErrorColor("");
      } else {
        setStartDateTime(dayjs(newValue));
        setStartDateTimeError(true);
        setStartDateTimeErrorColor("#d32f2f");
      }
    } else {
      setStartDateTime(dayjs(newValue));
    }
  };

  const handleOnSaveAction = async (emailStatus, emailStatusSource = "") => {
    if (!selectedRecipient.length && selectedCustomNames === null) {
      showAlert(
        true,
        "error",
        "Please choose either recipients or provide additional email IDs."
      );
      return;
    }

    if (
      (selectedCustomNames !== null && isValidEmailIds === "") ||
      isValidEmailIds === "invalid"
    ) {
      showAlert(
        true,
        "error",
        "Please verify the additional email IDs to proceed further."
      );
      return;
    }

    if (
      emailStatus === 1 &&
      emailStatusSource === "schedule" &&
      !startDateTime
    ) {
      showAlert(true, "error", "Please select the schedule date.");
      return;
    }

    const isValidUser = selectedUsers.length !== 0;
    const isValidFromName = selectedFromName.trim() !== "";
    const isValidSubjectName = selectedSubjectName.trim() !== "";
    const isValidEmailBody = selectedEmailBody.trim() !== "";

    if (!isValidFromName || !isValidSubjectName || !isValidEmailBody) {
      setIsValidUser(!isValidUser);
      setFromNameError(!isValidFromName);
      setSubjectNameError(!isValidSubjectName);
      setEmailBodyError(!isValidEmailBody);
      return;
    }

    // Reset Error object to default.
    resetErrorStates();

    try {
      setLoading(true);

      await Promise.all(
        attachments
          ?.filter((file) => file?.fileName && !file.isAlreadyUploaded)
          .map(async (file) => {
            try {
              await uploadGameScoreFileToBlob(file.fileName);
            } catch (error) {
              throw new Error(`File upload failed: ${file.fileName}`);
            }
          })
      );

      const payload = createPayload(emailStatus);
      const response =
        params?.notificationID > 0
          ? await EmailNotificationService.updateEmailNotificationDetails(
              payload
            )
          : await EmailNotificationService.insertEmailNotificationDetails(
              payload
            );

      //Send Notification only for SEND action
      let msg = "Data saved successfully";
      if (emailStatus === 1 && emailStatusSource === "") {
        await EmailNotificationService.sendImmediateNotification(
          "",
          response.data.id,
          user.loginID
        );

        msg = "Email sent successfully";
      }

      showAlert(true, "success", msg);
      //resetForm();
      //handleBackNavigation();
    } catch (error) {
      showAlert(true, "error", `Error while saving the data.`);
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackNavigation = (frequ = "auto") => {
    const shouldNavigate = params?.notificationID > 0 || frequ === "auto";

    if (!shouldNavigate && isFormDataEntered()) {
      setOpenNavigationConfirmDialog(true);
      return;
    }

    navigate(RouteConstants.MANAGE_EMAIL_NOTIFICATION);
  };

  const handleNavigationDialogClose = () => {
    setOpenNavigationConfirmDialog(false);
  };

  const handleNavigationDialogAgree = () => {
    setOpenNavigationConfirmDialog(false);
    navigate(`${RouteConstants.MANAGE_EMAIL_NOTIFICATION}`);
  };

  //... Utility to reset error states.
  const resetErrorStates = () => {
    setIsValidRecipients(false);
    setIsValidUser(false);
    setFromNameError(false);
    setSubjectNameError(false);
    setEmailBodyError(false);
  };

  //... Utility to reset form states.
  const resetForm = () => {
    setSelectedRecipient([]);
    setSelectedUsers([]);
    setSelectedCustomNames(null);
    setSSelectedFromName("");
    setSSelectedSubjectName("");
    setSelectedEmailBody("");
    setAttachments([]);
    setStartDateTime(null);
    setIsValidEmailIds("");
  };

  //... Utility to show alert notification.
  const showAlert = (error, type, message) => {
    setAlert(error);
    setAlertType(type);
    setAlertMessage(message);
  };

  //... Utility to create payload.
  const createPayload = (emailStatus = 0) => {
    const fileNames = attachments.map(
      ({ fileName: { name }, finalFileName }) => ({
        guidFileName: name,
        sourceFileName: finalFileName,
      })
    );

    // const fileNames = attachments.map((files) => {
    //   if (files.isAlreadyUploaded) {
    //     return {
    //       guidFileName: files.,
    //       sourceFileName: finalFileName,
    //     };
    //   } else {
    //   }
    // });

    const isNotificationIDPositive = params?.notificationID > 0;

    return {
      id: isNotificationIDPositive ? params?.notificationID : 0,
      fromName: selectedFromName,
      dL_Options: String(selectedRecipient),
      additional_To: selectedCustomNames,
      subject: selectedSubjectName,
      body: selectedEmailBody,
      scheduledDate: startDateTime,
      attachmentJSON: fileNames.length > 0 ? JSON.stringify(fileNames) : "",
      holdEmail: 0,
      emailStatus,
      ...(isNotificationIDPositive
        ? {
            modifiedBy: user.loginID,
            modifiedDate: new Date(),
            createdBy: null,
            createdDate: null,
          }
        : {
            createdBy: user.loginID,
            createdDate: new Date(),
            modifiedBy: null,
            modifiedDate: null,
          }),
    };
  };

  //... Utility to dlList enumration.
  async function getEnumerationData() {
    try {
      setLoading(true);
      const [DLList, EmailStatusList, EmailFromList] = await Promise.all([
        EmailNotificationService.getDLList(),
        EmailNotificationService.getEmailStatusList(),
        EmailNotificationService.getFromNameList(),
      ]);
      setLoading(false);
      setRecipientList(DLList.data || []);
      setEmailStatusList(EmailStatusList.data || []);
      setEmailFromList(EmailFromList.data || []);
    } catch (error) {
      console.error(`Error fetching enumeration data:`, error);
      setLoading(false);
    }
  }

  //... Service call to get notification details by ID.
  const getNotificationDetailsByID = async (notificationID) => {
    try {
      const notificationDetails =
        await EmailNotificationService.getNotificationDetailsByID(
          notificationID
        );

      if (notificationDetails.data) {
        const {
          dL_Options,
          additional_To,
          fromName,
          subject,
          body,
          scheduledDate,
          attachmentJSON,
        } = notificationDetails.data;

        const attachedFiles = attachmentJSON ? JSON.parse(attachmentJSON) : [];
        const fileNames = attachedFiles.map((file) => {
          const extension = file.sourceFileName.substring(
            file.sourceFileName.lastIndexOf(".") + 1
          );
          return {
            displayFileName: file.sourceFileName.replace(`.${extension}`, ""),
            fileName: { name: file.guidFileName },
            finalFileName: file.sourceFileName,
            isAlreadyUploaded: true,
          };
        });
        setSelectedRecipient(
          (dL_Options?.split(",") || []).map((value) => parseInt(value, 10))
        );
        setSelectedCustomNames(additional_To || null);
        setSSelectedFromName(fromName || "");
        setSSelectedSubjectName(subject || "");
        setSelectedEmailBody(body || "");
        setAttachments(fileNames || []);
        // setStartDateTime(scheduledDate ? dayjs(scheduledDate) : null);
        setStartDateTime(scheduledDate ? new Date(scheduledDate + "Z") : null);

        if (additional_To) {
          validateEmails(additional_To);
        }
      }
    } catch (error) {
      console.log("Error while fetching notification details: ", error);
    }
  };

  //... Utility to validate additional email-Id's.
  const validateEmails = (selectedCustomNames = "") => {
    const emailList = selectedCustomNames?.split(";").filter(Boolean) || [];

    if (emailList.length === 0) {
      setIsValidEmailIds("");
      setCustomEmailError(false);
      setSelectedCustomNames(null);
      return;
    }

    const invalidEmails = [];
    const validEmails = [];

    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w\w+)+$/;

    emailList.forEach((email) => {
      const trimmedEmail = email.trim();
      if (trimmedEmail && emailRegex.test(trimmedEmail)) {
        validEmails.push(trimmedEmail.toLowerCase());
      } else {
        invalidEmails.push(trimmedEmail);
      }
    });

    setIsValidEmailIds(invalidEmails.length > 0 ? "invalid" : "validated");
    setCustomEmailError(
      invalidEmails.length > 0
        ? `'${invalidEmails.join(",")}' is Invalid Email Id`
        : false
    );
  };

  //... Utility to check is form filled by user.
  const isFormDataEntered = () => {
    return (
      selectedRecipient.length > 0 ||
      selectedCustomNames !== null ||
      selectedFromName !== "" ||
      selectedSubjectName !== "" ||
      selectedEmailBody !== "" ||
      attachments.length > 0 ||
      startDateTime !== null
    );
  };

  //... Render utility to render title.
  const RenderTitle = ({ title }) => {
    return (
      <Box className={classes.inputContainer}>
        <Typography
          variant="body1"
          component="label"
          gutterBottom
          fontWeight={800}
        >
          {`${title} :`}
        </Typography>
      </Box>
    );
  };

  //... Render utility to render icon-button.
  const StatusIconButton = ({ color, icon, label }) => (
    <IconButton style={{ marginLeft: "8px" }} disabled>
      {React.cloneElement(icon, { style: { color } })}
      <Typography variant="button" style={{ marginLeft: "4px", color }}>
        {label}
      </Typography>
    </IconButton>
  );

  return (
    <>
      <Loader isLoading={loading} />
      <AppBar position="static" className={classes.appbarBackground}>
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box display="flex" my={0.8} alignItems="center">
            <Typography variant="h6" className={classes.title}>
              Send Email Notification
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              columnGap: "10px",
              padding: "5px",
            }}
          >
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => handleBackNavigation("mannual")}
            >
              <ReplyIcon style={{ marginRight: "5px" }} />
              <Hidden smDown> Back</Hidden>
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Container maxWidth={false} className={classes.centerContainer}>
        <Paper elevation={3} className={classes.paperContainer}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3} justify="space-around">
              {/* Add Recipients Section */}
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <RenderTitle title="Add Recipients" />
              </Grid>
              <Grid item lg={9} md={8} sm={6} xs={12}>
                <Stack spacing={2} sx={{ width: { xs: "100%", sm: "60%" } }}>
                  <FormControl fullWidth>
                    <InputLabel id="multi-select-label">
                      Select Recipients
                    </InputLabel>
                    <Select
                      labelId="multi-select-label"
                      id="multi-select"
                      multiple
                      value={selectedRecipient}
                      onChange={handleRecipientChange}
                      renderValue={(selected) =>
                        selected
                          .map((childID) => {
                            const selectedChild = recipientList.find(
                              (option) => option.childID === childID
                            );
                            return selectedChild ? selectedChild.childName : "";
                          })
                          .join(", ")
                      }
                      label="Select Recipients"
                    >
                      {recipientList &&
                        recipientList.length > 0 &&
                        recipientList?.map((option) => (
                          <MenuItem
                            key={option.childID}
                            value={option.childID}
                            disabled={
                              selectedRecipient.length === 1 &&
                              selectedRecipient.includes(0) &&
                              option.childID !== 0
                            }
                          >
                            <Checkbox
                              checked={selectedRecipient.includes(
                                option.childID
                              )}
                            />
                            <ListItemText primary={option.childName} />
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  {/* <FormControl fullWidth>
                      <Autocomplete
                        multiple
                        id="search-select"
                        options={options}
                        value={selectedUsers}
                        onChange={handleUserChange}
                        isOptionEqualToValue={(option, value) =>
                          option === value
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Users"
                            variant="outlined"
                            error={isValidUser}
                            helperText={
                              isValidUser && "Please select at least one user"
                            }
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip label={option} {...getTagProps({ index })} />
                          ))
                        }
                      />
                    </FormControl> */}

                  <FormControl fullWidth>
                    <Typography
                      variant="body2"
                      style={{
                        marginBottom: "16px",
                        marginLeft: "5px",
                        fontWeight: "bold",
                      }}
                    >
                      Note: Please separate every email address with semicolon
                      (;)
                    </Typography>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <TextField
                        id="outlined-basic"
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        label={
                          <Typography variant="subtitle2">
                            Additional Email Id's
                          </Typography>
                        }
                        error={Boolean(customEmailError)}
                        helperText={customEmailError}
                        value={selectedCustomNames}
                        onChange={(e) => handleCustomChange(e)}
                        onBlur={handleCustomBlur}
                        InputLabelProps={{
                          shrink: true,
                          style: { marginTop: "-8px" }, // Adjust the marginTop as needed
                        }}
                        inputProps={{
                          style: { paddingTop: "16px" }, // Adjust the paddingTop as needed
                        }}
                      />

                      {isValidEmailIds === "validated" ? (
                        <StatusIconButton
                          color="green"
                          icon={<VerifiedIcon />}
                          label="Validated"
                        />
                      ) : isValidEmailIds === "invalid" ? (
                        <StatusIconButton
                          color="red"
                          icon={<HighlightOffIcon />}
                          label="Invalid"
                        />
                      ) : null}
                    </Box>
                  </FormControl>
                </Stack>
              </Grid>

              {/* From Name Section */}
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <RenderTitle title="From Name" />
              </Grid>
              <Grid item lg={9} md={8} sm={6} xs={12}>
                <Box sx={{ display: "flex", width: "100%" }}>
                  <Box
                    sx={{
                      width: { xs: "100%", sm: "60%" },
                      paddingRight: "16px",
                    }}
                  >
                    <FormControl size="small" fullWidth>
                      <InputLabel id="demo-select-small-label">
                        From Name
                      </InputLabel>
                      <Select
                        labelId="demo-select-small-label"
                        id="demo-select-small"
                        value={selectedFromName}
                        size="small"
                        label="From Name"
                        onChange={(e) => setSSelectedFromName(e.target.value)}
                        sx={{
                          backgroundColor: "#fff",
                          border: "none",
                          fontSize: "0.8rem",
                        }}
                        error={fromNameError}
                      >
                        {emailFromList &&
                          emailFromList.length > 0 &&
                          emailFromList.map((item) => (
                            <MenuItem key={item.childID} value={item.childName}>
                              {item.childName}
                            </MenuItem>
                          ))}
                      </Select>
                      <FormHelperText style={{ color: "red" }}>
                        {fromNameError ? "Please enter a valid From Name" : ""}
                      </FormHelperText>
                    </FormControl>
                  </Box>
                  {/* <Box sx={{ width: "40%" }}>
                    <FormControl
                      size="small"
                      fullWidth
                      className="date-time-picker"
                    >
                      <DateTimePicker
                        clearable
                        //minDateTime={today}
                        label="Schedule Date Time"
                        value={startDateTime}
                        onChange={handleStartDateTimeChange}
                        slotProps={{ textField: { size: "small" } }}
                        InputProps={{
                          style: {
                            maxHeight: 40,
                          },
                          error: startDateTimeError,
                        }}
                        renderInput={(params) => (
                          <TextField
                            InputLabelProps={{
                              style: { color: startDateTimeErrorColor },
                              error: startDateTimeError,
                            }}
                            {...params}
                          />
                        )}
                      />
                    </FormControl>
                  </Box> */}
                </Box>
              </Grid>

              {/* Subject Line Section */}
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <RenderTitle title="Subject Line" />
              </Grid>
              <Grid item lg={9} md={8} sm={6} xs={12}>
                <Box sx={{ width: { xs: "100%", sm: "60%" } }}>
                  <FormControl size="small" fullWidth>
                    <TextField
                      label="Subject Line"
                      placeholder="Please enter a Subject Line"
                      size="small"
                      value={selectedSubjectName}
                      onChange={(e) => setSSelectedSubjectName(e.target.value)}
                      error={subjectNameError}
                      helperText={
                        subjectNameError
                          ? "Please enter a valid Subject Line"
                          : ""
                      }
                    />
                  </FormControl>
                </Box>
              </Grid>

              {/* Body Section */}
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <RenderTitle title="Email Body" />
              </Grid>
              <Grid item lg={9} md={8} sm={6} xs={12}>
                <Box sx={{ width: "100%" }}>
                  <FormControl size="small" fullWidth>
                    {editorLoading ? (
                      // Show a loading indicator or placeholder text
                      <div>Loading Editor...</div>
                    ) : (
                      <>
                        <CKEditor
                          config={{
                            versionCheck: false,
                          }}
                          data={selectedEmailBody}
                          onChange={(e) => handleEditorChange(e)}
                        />
                        {emailBodyError ? (
                          <span
                            style={{
                              color: "red",
                              fontSize: "0.75rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            Please enter a valid Email Body
                          </span>
                        ) : null}
                      </>
                    )}
                  </FormControl>
                </Box>
              </Grid>

              {/* Attachments Section */}
              <Grid item lg={3} md={4} sm={6} xs={12}>
                <RenderTitle title="Attachments" />
              </Grid>
              <Grid item lg={9} md={8} sm={6} xs={12}>
                <StyledBox>
                  <UploadContainer>
                    <label htmlFor="upload-input">
                      <input
                        id="upload-input"
                        type="file"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                      />
                      <IconButton component="span">
                        <CloudUploadIcon />
                      </IconButton>
                    </label>
                  </UploadContainer>
                  <StyledList>
                    {attachments.map((file, index) => (
                      <StyledListItem key={index}>
                        <ListItemText primary={file.displayFileName} />
                        <IconButton
                          sx={{ color: "red" }}
                          onClick={() => handleRemoveFile(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </StyledListItem>
                    ))}
                  </StyledList>
                </StyledBox>
              </Grid>

              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Box className={classes.buttonContainer}>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<DraftsIcon />}
                      onClick={() => handleOnSaveAction(0)}
                    >
                      Save as draft
                    </Button>
                    {/* <Button
                      variant="contained"
                      color="info"
                      startIcon={<ScheduleIcon />}
                      onClick={() => handleOnSaveAction(1, "schedule")}
                    >
                      Schedule
                    </Button> */}
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SendIcon />}
                      onClick={() => handleOnSaveAction(1)}
                    >
                      Send
                    </Button>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Paper>
        <Dialog
          open={openNavigationConfirmDialog}
          TransitionComponent={Transition}
          keepMounted
          onClose={handleNavigationDialogClose}
          aria-describedby="alert-delete-dialog-slide-description"
          maxWidth="lg"
          PaperProps={{
            style: {
              width: "40%",
            },
          }}
        >
          <DialogTitle>Confirmation</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-slide-description">
              If you proceed, entered data will be lost. Would you like to
              continue, or would you prefer to save the details before
              proceeding?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleNavigationDialogClose}
              variant="contained"
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleNavigationDialogAgree}
              variant="contained"
              color="primary"
              autoFocus
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={alert}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          autoHideDuration={3000}
          onClose={() => setAlert(false)}
        >
          <Alert
            onClose={() => setAlert(false)}
            severity={alertType}
            sx={{ width: "100%" }}
          >
            {alertMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default EmailNotification;
